import { useCallback } from 'react'
import {
  useCheckout,
  useUpdatePlan,
  isPaymentMethodRequiredError,
  type CheckoutOptions,
  type CheckoutPlan,
  type UpdatePlanTarget,
} from './useCheckout'
import { useEnrichedBilling } from './useEnrichedBilling'

/**
 * A purchase where the in-app plan target and the checkout body differ (trial
 * flows: the trial plan in-app, the tier's trial checkout otherwise). A bare
 * tier means both halves are "switch to that tier".
 */
export interface ChangeV2PlanRequest {
  planRequest: UpdatePlanTarget
  checkoutBody: CheckoutOptions
}

export interface ChangeV2PlanHandlers {
  /** The plan change succeeded; a failed refresh must not retry the purchase. */
  onPlanChanged: (billingRefreshed: boolean) => void
  /** No card on file, so a Stripe checkout for the plan was opened instead. */
  onCheckoutOpened: () => void
  onError: (step: 'plan-change' | 'checkout', error: Error) => void
}

/**
 * Commit flow for every v2 purchase — the settings PlanPicker, the
 * manage-subscription modal, and the paywall CTAs (upgrade, renew): the server
 * decides whether money is due now and answers "payment method required" when
 * it is and no card is on file; only then does checkout open to collect one. A
 * scheduled downgrade, an un-cancel or a card-less trial start needs no card, so
 * no local card check runs first.
 */
export const useChangeV2Plan = () => {
  const { mutate: updatePlan, isPending: isUpdatePlanPending } = useUpdatePlan()
  const { mutate: createCheckout, isPending: isCheckoutPending } = useCheckout()
  // A display hint for callers (whether a change charges immediately); the server decides.
  const hasPaymentMethod = useEnrichedBilling().billingProfile?.hasDefaultPaymentMethod ?? false

  const changePlan = useCallback(
    (request: CheckoutPlan | ChangeV2PlanRequest, handlers: ChangeV2PlanHandlers) => {
      const { planRequest, checkoutBody } =
        typeof request === 'string' ? { planRequest: request, checkoutBody: { plan: request } } : request

      const openCheckout = () =>
        createCheckout(checkoutBody, {
          onSuccess: () => handlers.onCheckoutOpened(),
          onError: (error) =>
            handlers.onError('checkout', error instanceof Error ? error : new Error('Failed to start checkout')),
        })

      updatePlan(planRequest, {
        onSuccess: (result) => handlers.onPlanChanged(result.billingRefreshed),
        onError: (error) => {
          if (isPaymentMethodRequiredError(error)) {
            openCheckout()
            return
          }
          handlers.onError('plan-change', error instanceof Error ? error : new Error('Failed to change plan'))
        },
      })
    },
    [updatePlan, createCheckout],
  )

  return { changePlan, hasPaymentMethod, isPending: isUpdatePlanPending || isCheckoutPending }
}
